/**
 * Query keys for TanStack Query. Centralized so that invalidation after mutations is consistent.
 */
export const queryKeys = {
  me: ["me"] as const,
  myTenants: ["my-tenants"] as const,
  branding: ["branding"] as const,
  clockState: ["me", "clock-state"] as const,
  workDays: (from: string, to: string) => ["me", "workdays", from, to] as const,
  allWorkDays: ["me", "workdays"] as const,
  balances: (asOf: string) => ["me", "balances", asOf] as const,
  allBalances: ["me", "balances"] as const,
  /** The own flexitime account of § 7 AZV (F-117); under the balances, so a booking reads it again. */
  azvFlexitime: (asOf: string) => ["me", "balances", "azv-flexitime", asOf] as const,
  corrections: (status: string) => ["corrections", status] as const,
  allCorrections: ["corrections"] as const,
  absences: (from: string, to: string) => ["absences", from, to] as const,
  allAbsences: ["absences"] as const,
  violations: (from: string, to: string, status: string) =>
    ["violations", from, to, status] as const,
  allViolations: ["violations"] as const,
  team: (date: string) => ["team", date] as const,
  establishments: ["master-data", "establishments"] as const,
  establishmentOverview: ["establishments", "overview"] as const,
  workTimeModels: ["master-data", "work-time-models"] as const,
  employees: ["master-data", "employees"] as const,
  employments: (employeeId: string) => ["master-data", "employments", employeeId] as const,
  teamDefinitions: ["master-data", "teams"] as const,
  leavePolicy: ["tenant", "leave-policy"] as const,
  leaveEntitlements: (employeeId: string) =>
    ["master-data", "leave-entitlements", employeeId] as const,
  leaveBookings: (employeeId: string, year: number) =>
    ["master-data", "leave-bookings", employeeId, year] as const,
  employeeLeaveBookings: (employeeId: string) =>
    ["master-data", "leave-bookings", employeeId] as const,
  allLeaveBookings: ["master-data", "leave-bookings"] as const,
  severeDisabilityPeriods: (employeeId: string) =>
    ["master-data", "severe-disability-periods", employeeId] as const,
  parentalLeavePeriods: (employeeId: string) =>
    ["master-data", "parental-leave-periods", employeeId] as const,
  leaveAccount: (employeeId: string | null, year: number) =>
    ["leave-account", employeeId ?? "me", year] as const,
  allLeaveAccounts: ["leave-account"] as const,
  /** The last closed leave year of an employee, for the hint at a fact recorded later (F-19). */
  closedLeaveYear: (employeeId: string) => ["leave-account", employeeId, "closed-year"] as const,
  leaveRelationships: (employeeId: string) =>
    ["master-data", "leave-relationships", employeeId] as const,
  allLeaveRelationships: ["master-data", "leave-relationships"] as const,
  /** Notices on remaining leave (T-164): own (`null`) or of an employee for HR. */
  leaveNotices: (employeeId: string | null) => ["leave-notices", employeeId ?? "me"] as const,
  allLeaveNotices: ["leave-notices"] as const,
  leaveNoticeOverview: (year: number) => ["leave-notices", "overview", year] as const,
  /** The year-end closing of a leave year (T-168). */
  leaveYearClosing: (year: number, verify = false) => ["leave-year-closing", year, verify] as const,
  allLeaveYearClosings: ["leave-year-closing"] as const,
  /** The closing of a month for the tenant (`null`) or an establishment (T-171). */
  monthClosing: (month: string, establishmentId: string | null) =>
    ["month-closing", month, establishmentId ?? "tenant"] as const,
  /** The months of persons the caller may approve (T-171). */
  monthApprovals: (month: string, establishmentId: string | null) =>
    ["month-closing", "approvals", month, establishmentId ?? "all"] as const,
  /** The month of one person before the approval (T-171). */
  monthApproval: (month: string, employeeId: string) =>
    ["month-closing", "approval", month, employeeId] as const,
  /** The caller's own month (T-171). */
  ownMonth: (month: string) => ["month-closing", "me", month] as const,
  /** The open notices on changes of locked months of every month (T-171). */
  openMonthChangeNotices: ["month-closing", "open-notices"] as const,
  allMonthClosings: ["month-closing"] as const,
  teamLeaveHints: ["team", "leave-hints"] as const,
  /** The notices on flexitime balances of federal civil servants for their immediate superior (F-116). */
  teamAzvBalanceNotices: ["team", "azv-balance-notices"] as const,
  projects: ["projects"] as const,
  project: (projectId: string) => ["projects", projectId] as const,
  /** A project report (T-169) for its query parameters. */
  projectReport: (query: Readonly<Record<string, string>>) =>
    ["project-report", JSON.stringify(query)] as const,
  projectPolicies: ["project-requirement-policies"] as const,
  setupChecklist: ["setup", "checklist"] as const,
  setupTemplates: ["setup", "templates"] as const,
  invitations: ["setup", "invitations"] as const,
  projectDays: (from: string, to: string) => ["me", "project-days", from, to] as const,
  allProjectDays: ["me", "project-days"] as const,
  projectPeriodLocks: ["project-period-locks"] as const,
  myProjectsOn: (date: string) => ["me", "projects", date] as const,
  attributionRequests: (scope: "all" | "pending") =>
    ["project-attribution-requests", scope] as const,
  allAttributionRequests: ["project-attribution-requests"] as const,
  /** Education leave of a person on a day (F-21): own (`null`) or of an employee for HR. */
  educationLeave: (employeeId: string | null, day: string | null) =>
    ["education-leave", employeeId ?? "me", day ?? "today"] as const,
  allEducationLeave: ["education-leave"] as const,
  /** The education leave rules of the federal states shipped with the application (F-21). */
  educationLeaveProfile: ["education-leave-profile"] as const,
};
